import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { StyledSelect } from 'components/UnifiedAlerts/EarlyWarningSystemRule/SharedComponents';
import { getEwsApproverDetails, EwsApproverDetailDto } from 'service/approvalWorkflow';

export interface AddEditSopModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { sopName: string; sopLink: string; approvalRequired: boolean; owner: string }) => Promise<void> | void;
  editingId?: string | number | null;
  isResubmit?: boolean;
  initialName?: string;
  initialLink?: string;
  initialOwner?: string;
  initialApprovalRequired?: boolean;
  loading?: boolean;
}

const DEFAULT_APPROVER_OPTIONS = [
  { value: 'Andrew Clan', label: 'Andrew Clan' },
  { value: 'John Doe (You)', label: 'John Doe (You)' },
  { value: 'Sarah Connor', label: 'Sarah Connor' },
  { value: 'Michael Scott', label: 'Michael Scott' },
  { value: 'Alex Morgan', label: 'Alex Morgan' },
];

const selectTheme = (outerTheme: any) =>
  createTheme(outerTheme, {
    zIndex: {
      modal: (outerTheme?.zIndex?.modal || 1300) + 20,
    },
  });

export const AddEditSopModal: React.FC<AddEditSopModalProps> = ({
  open,
  onClose,
  onSave,
  editingId = null,
  isResubmit = false,
  initialName = '',
  initialLink = '',
  initialOwner = '',
  initialApprovalRequired = true,
  loading = false,
}) => {
  const [sopName, setSopName] = useState(initialName);
  const [sopLink, setSopLink] = useState(initialLink);
  const [approvalRequired, setApprovalRequired] = useState(initialApprovalRequired);
  const [owner, setOwner] = useState<string>(initialOwner || '');
  const [approvers, setApprovers] = useState<EwsApproverDetailDto[]>([]);
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [errors, setErrors] = useState<{ sopName?: string; sopLink?: string; owner?: string }>({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setSopName(initialName || '');
      setSopLink(initialLink || '');
      setOwner(initialOwner || '');
      setApprovalRequired(initialApprovalRequired ?? true);
      setErrors({});
      setSubmitted(false);
    }
  }, [open, initialName, initialLink, initialOwner, initialApprovalRequired]);

  useEffect(() => {
    let isMounted = true;
    if (open) {
      const fetchApprovers = async () => {
        try {
          setLoadingApprovers(true);
          const approversResp = await getEwsApproverDetails();
          const list = Array.isArray(approversResp)
            ? approversResp
            : Array.isArray(approversResp?.data)
              ? approversResp.data
              : Array.isArray(approversResp?.data?.data)
                ? approversResp.data.data
                : [];
          if (isMounted && list.length > 0) {
            setApprovers(list);
          }
        } catch (err) {
          console.log('Backend approvers fetch error in AddEditSopModal:', err);
        } finally {
          if (isMounted) {
            setLoadingApprovers(false);
          }
        }
      };
      fetchApprovers();
    }
    return () => {
      isMounted = false;
    };
  }, [open]);

  const approverOptions = useMemo(() => {
    if (approvers.length > 0) {
      return approvers.map((a) => ({
        value: a.email,
        label: a.role ? `${a.email} (${a.role})` : a.email,
      }));
    }
    return DEFAULT_APPROVER_OPTIONS;
  }, [approvers]);

  const finalApproverOptions = useMemo(() => {
    const base = approverOptions || [];
    if (owner && !base.some((o) => o.value.toLowerCase() === owner.toLowerCase())) {
      return [{ value: owner, label: owner }, ...base];
    }
    return base;
  }, [approverOptions, owner]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleNameChange = (val: string) => {
    setSopName(val);
    if (submitted && val.trim()) {
      setErrors((prev) => ({ ...prev, sopName: undefined }));
    }
  };

  const handleLinkChange = (val: string) => {
    setSopLink(val);
    if (submitted && val.trim()) {
      setErrors((prev) => ({ ...prev, sopLink: undefined }));
    }
  };

  const handleApprovalRequiredChange = (checked: boolean) => {
    setApprovalRequired(checked);
    if (!checked) {
      setErrors((prev) => ({ ...prev, owner: undefined }));
    } else if (submitted && !owner.trim()) {
      setErrors((prev) => ({ ...prev, owner: 'Selecting an approver is mandatory' }));
    }
  };

  const handleOwnerChange = (val: string) => {
    setOwner(val);
    if (submitted && val.trim()) {
      setErrors((prev) => ({ ...prev, owner: undefined }));
    }
  };

  const isChanged = useMemo(() => {
    const nameChanged = sopName.trim() !== (initialName || '').trim();
    const linkChanged = sopLink.trim() !== (initialLink || '').trim();
    const ownerChanged = (approvalRequired ? owner.trim() : '') !== (initialOwner || '').trim();
    const approvalRequiredChanged = Boolean(approvalRequired) !== Boolean(initialApprovalRequired ?? true);
    return nameChanged || linkChanged || ownerChanged || approvalRequiredChanged;
  }, [sopName, initialName, sopLink, initialLink, owner, initialOwner, approvalRequired, initialApprovalRequired]);

  const isEditOrResubmitMode = Boolean(editingId || isResubmit);
  const isSubmitDisabled = loading || (isEditOrResubmitMode && !isChanged);

  const validate = () => {
    const errs: { sopName?: string; sopLink?: string; owner?: string } = {};
    if (!sopName.trim()) {
      errs.sopName = 'SOP Name is mandatory';
    }
    if (!sopLink.trim()) {
      errs.sopLink = 'SOP Link is mandatory';
    }
    if (approvalRequired && !owner.trim()) {
      errs.owner = 'Selecting an approver is mandatory';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!validate()) return;
    if (isSubmitDisabled) return;

    await onSave({
      sopName: sopName.trim(),
      sopLink: sopLink.trim(),
      approvalRequired,
      owner: approvalRequired ? owner.trim() : '',
    });
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#FFFFFF',
      height: '42px',
      fontSize: '0.875rem',
      borderRadius: '4px',
      '& fieldset': {
        borderColor: '#C8D7EC !important',
      },
      '&:hover fieldset': {
        borderColor: '#009BD8 !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#009BD8 !important',
        borderWidth: '1.5px !important',
      },
      '&.Mui-error fieldset': {
        borderColor: '#d32f2f !important',
      },
      '&.Mui-disabled': {
        bgcolor: '#F5F7FA',
        '& fieldset': {
          borderColor: '#E2E8F0 !important',
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: '#64748B !important',
      fontSize: '0.875rem',
      fontWeight: 500,
      bgcolor: '#F4F8FD',
      px: 0.5,
      '&.Mui-focused': {
        color: '#009BD8 !important',
      },
      '&.Mui-error': {
        color: '#d32f2f !important',
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
      mt: 0.5,
      mx: 0.5,
      color: '#d32f2f !important',
    },
    '& .MuiInputBase-input': {
      color: '#0D2B4E',
      py: 0,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 10,
      }}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '900px',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 12px 36px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      {/* Dark Navy Blue Title Bar */}
      <DialogTitle
        sx={{
          bgcolor: '#0D2B4E',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1.05rem',
            color: '#FFFFFF !important',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {isResubmit ? 'RESUBMIT SOP' : editingId ? 'UPDATE SOP' : 'ADD NEW SOP'}
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#FFFFFF !important',
            p: 0.5,
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>

      {/* Light Blue-Gray Form Body */}
      <DialogContent sx={{ bgcolor: '#F4F8FD', p: 3.5, pt: '28px !important' }}>
        {/* Row 1: SOP Name & SOP Link */}
        <Box sx={{ display: 'flex', gap: 2.5, mb: 3 }}>
          <TextField
            label="SOP Name*"
            variant="outlined"
            fullWidth
            size="small"
            value={sopName}
            onChange={(e) => handleNameChange(e.target.value)}
            disabled={loading}
            error={Boolean(errors.sopName)}
            helperText={errors.sopName}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <TextField
            label="SOP Link*"
            variant="outlined"
            fullWidth
            size="small"
            value={sopLink}
            onChange={(e) => handleLinkChange(e.target.value)}
            disabled={loading}
            error={Boolean(errors.sopLink)}
            helperText={errors.sopLink}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
        </Box>

        {/* Row 2: Approval Required Checkbox & Owner Dropdown */}
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              pt: 0.5,
              opacity: (loading || !!editingId || isResubmit) ? 0.45 : 1,
              cursor: (loading || !!editingId || isResubmit) ? 'not-allowed' : 'default',
              transition: 'opacity 0.2s ease',
            }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={approvalRequired}
                  onChange={(e) => handleApprovalRequiredChange(e.target.checked)}
                  disabled={loading || !!editingId || isResubmit}
                  sx={{
                    color: '#009BD8',
                    '&.Mui-checked': {
                      color: '#009BD8',
                    },
                    p: 0.75,
                  }}
                />
              }
              label={
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0D2B4E' }}>
                  Approval Required?
                </Typography>
              }
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <ThemeProvider theme={selectTheme}>
              <StyledSelect
                label={approvalRequired ? "Search/Select Owner for Approval*" : "Search/Select Owner for Approval"}
                placeholder={loadingApprovers ? 'Loading approvers...' : 'Select Approver'}
                value={owner}
                onChange={(val) => handleOwnerChange(val)}
                options={finalApproverOptions}
                disabled={!approvalRequired || loading || loadingApprovers}
                hideLabel={false}
                bgColor="#ffffff"
                height={42}
                borderRadius={1}
                sx={{
                  '& .MuiInputLabel-root': {
                    bgcolor: '#F4F8FD',
                    px: 0.5,
                    color: errors.owner ? '#d32f2f !important' : '#64748B !important',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: errors.owner ? '#d32f2f !important' : '#009BD8 !important',
                    },
                  },
                  ...(errors.owner
                    ? {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d32f2f !important',
                        },
                      }
                    : {}),
                }}
              />
            </ThemeProvider>
            {errors.owner && (
              <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                {errors.owner}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Row 3 / Footer: Italic note on left, Cancel & Save on right */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mt: 2,
            pt: 1,
          }}
        >
          <Box sx={{ minHeight: '20px' }}>
            {approvalRequired && (
              <Typography
                sx={{
                  fontStyle: 'italic',
                  fontSize: '0.85rem',
                  color: '#7A8CA4',
                }}
              >
                Email will be sent to selected owner for approval.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="text"
              onClick={handleClose}
              disabled={loading}
              sx={{
                color: '#009BD8 !important',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                px: 2,
                '&:hover': { bgcolor: 'rgba(0, 155, 216, 0.08)' },
              }}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
              sx={{
                bgcolor: '#009BD8 !important',
                color: '#FFFFFF !important',
                textTransform: 'uppercase',
                px: 3,
                height: 40,
                fontWeight: 600,
                fontSize: '0.875rem',
                borderRadius: '4px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#0085BA !important',
                  boxShadow: '0 2px 8px rgba(0, 155, 216, 0.3)',
                },
                '&.Mui-disabled': {
                  bgcolor: '#CBD5E1 !important',
                  color: '#FFFFFF !important',
                  opacity: 0.8,
                },
              }}
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: '#FFFFFF' }} />
              ) : isResubmit ? (
                'RESUBMIT SOP'
              ) : editingId ? (
                'UPDATE SOP'
              ) : approvalRequired ? (
                'SAVE & SEND FOR APPROVAL'
              ) : (
                'SAVE SOP'
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditSopModal;
